// URL-fragment handling for forecast COPY links. No React (lib/ convention).
//
// Split out of state.js deliberately: `state.js` holds pure derivations, and
// `clearFragment` below MUTATES `window.history` — it is a view-layer effect, not
// a derivation, and a file named state.js oversold that. Both functions here are
// about the address bar rather than about a link's domain state.
//
// THE SECRET LIVES ONLY IN THE FRAGMENT. Fragments are never transmitted by
// browsers, which is the whole mechanism (§5.1) — so the secret must never be
// copied into a query string, a `redirect_uri`, the OIDC `state`, or a log line.
// The `window` is always INJECTED rather than read from module scope, so nothing
// here can touch the real address bar in a test.
//
// Task 4's `sessionStorage` stash of the secret across the auth round trip
// belongs beside this file (a new sibling module), not inside it: stashing is
// storage I/O with a lifetime, distinct from parsing one string and rewriting one
// URL.

// The mint secret is base64url of 32 random bytes
// (`crypto.randomBytes(32).toString("base64url")`,
// server/handlers/copyLinks.js:304), so a legitimate fragment is exactly this
// alphabet and nothing else.
const SECRET_RE = /^[A-Za-z0-9_-]+$/;

/**
 * Read the link secret out of a URL fragment.
 *
 * Validated against the base64url alphabet rather than returned verbatim. The
 * case that makes this load-bearing: after an auth round trip the fragment can
 * carry the PROVIDER's own response params (`#state=…&access_token=…`). Handing
 * that back as "the secret" would POST someone's access token to our redeem
 * endpoint. A fragment we do not recognise is not a secret.
 *
 * Also rejects percent-encoded input: base64url needs no encoding, so `%3D`
 * means something re-encoded the fragment and we no longer know what we hold.
 *
 * @param {unknown} hash  `window.location.hash` (leading "#" optional)
 * @returns {string|null} the secret, or null if there isn't a plausible one
 */
export function readFragmentSecret(hash) {
  if (typeof hash !== "string") return null;
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  return SECRET_RE.test(raw) ? raw : null;
}

/**
 * Drop the fragment from the address bar, keeping path + query.
 *
 * `replaceState`, never `pushState`: a history entry holding the secret is one
 * back-navigation (or one screenshot of the URL bar) away from leaking it.
 *
 * Returns whether it actually happened, and never throws — a caller clearing the
 * secret is usually mid-redeem, and a hostile-environment `replaceState`
 * (SecurityError, rate limit) must not abort the redemption. A `false` return is
 * the caller's cue that the secret is still on screen; it is not something to log
 * verbatim.
 *
 * @param {{location?: object, history?: object}|null|undefined} win  window (injected)
 * @returns {boolean} true if the fragment was replaced
 */
export function clearFragment(win) {
  const loc = win?.location;
  if (!loc || typeof loc.pathname !== "string") return false;
  if (typeof win.history?.replaceState !== "function") return false;
  try {
    win.history.replaceState(null, "", `${loc.pathname}${loc.search || ""}`);
  } catch {
    return false;
  }
  return true;
}
